import { useState, useEffect } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaUserPlus, FaTimes, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import jornadaService from '../../services/jornadaService';
import futbolistaEnEquipoService from '../../services/futbolistaEnEquipoService';
import PositionSelector from './PositionSelector';

/**
 * Modal para añadir futbolista a un equipo específico
 * Estilo similar al modal de asignar entrenador
 */
const AddPlayerModal = ({
  show,
  onHide,
  selectedTeam,
  onPlayerAdded
}) => {
  const [futbolistasDisponibles, setFutbolistasDisponibles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null); // Para mostrar selector de posición
  const [searchTerm, setSearchTerm] = useState(''); // Para el buscador

  // Cargar futbolistas disponibles cuando se abre el modal
  useEffect(() => {
    if (show && selectedTeam) {
      console.log('🔍 Debug AddPlayerModal useEffect:');
      console.log('- selectedTeam:', selectedTeam);
      console.log('- selectedTeam.jornadaId:', selectedTeam.jornadaId);
      console.log('- selectedTeam.sedeId:', selectedTeam.sedeId);
      loadFutbolistasDisponibles();
    } else if (!show) {
      // Limpiar estado cuando se cierra el modal
      setSelectedPlayer(null);
      setFutbolistasDisponibles([]);
      setSearchTerm(''); // Limpiar búsqueda
    }
  }, [show, selectedTeam]);

  const loadFutbolistasDisponibles = async () => {
    setLoading(true);
    try {
      const response = await jornadaService.getFutbolistasDisponiblesManual(
        selectedTeam.jornadaId, 
        selectedTeam.sedeId
      );
      if (response.success) {
        // Filtrar jugadores duplicados por futbolista_id
        const jugadoresUnicos = response.data.filter((jugador, index, array) =>
          array.findIndex(j => j.futbolista_id === jugador.futbolista_id) === index
        );

        console.log(`✅ Datos originales: ${response.data.length}, después de filtrar duplicados: ${jugadoresUnicos.length}`);
        if (response.data.length !== jugadoresUnicos.length) {
          console.warn('⚠️ Se encontraron jugadores duplicados que fueron filtrados');
        }

        setFutbolistasDisponibles(jugadoresUnicos);
        console.log(`✅ Cargados ${jugadoresUnicos.length} futbolistas disponibles:`, response.breakdown);
      } else {
        throw new Error(response.message || 'Error al cargar futbolistas');
      }
    } catch (error) {
      console.error('Error cargando futbolistas disponibles:', error);
      toast.error('Error al cargar futbolistas disponibles');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlayer = (futbolistaId) => {
    const jugador = futbolistasDisponibles.find(j => j.futbolista_id === futbolistaId);
    if (jugador) {
      setSelectedPlayer(jugador);
    }
  };

  const handleAssignPlayerWithPosition = async (posicionAsignada) => {
    if (!selectedTeam || !selectedPlayer) {
      toast.error('No se ha especificado el equipo destino o jugador');
      return;
    }

    try {
      setAssigning(true);

      console.log('🔍 Debug handleAssignPlayerWithPosition:');
      console.log('- futbolistaId:', selectedPlayer.futbolista_id);
      console.log('- jugador:', selectedPlayer);
      console.log('- selectedTeam:', selectedTeam);
      console.log('- equipo_id:', selectedTeam.equipo_id);
      console.log('- posicionAsignada:', posicionAsignada);

      // Usar el servicio de futbolistaEnEquipo para asignar directamente
      const response = await futbolistaEnEquipoService.asignarJugador({
        futbolista_id: selectedPlayer.futbolista_id,
        equipo_id: selectedTeam.equipo_id,
        posicion_asignada: posicionAsignada,
        es_titular: false
      });

      console.log('🔍 Debug response:', response);

      if (response.success) {
        toast.success(`${selectedPlayer.nombre} asignado exitosamente al ${selectedTeam.nombre}`);

        // Notificar al componente padre
        if (onPlayerAdded) {
          onPlayerAdded(selectedPlayer.futbolista_id);
        }

        // Cerrar modal
        onHide();
      } else {
        throw new Error(response.error || 'Error desconocido');
      }
    } catch (error) {
      console.error('❌ Error asignando jugador:', error);
      console.error('❌ Error details:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error(`Error al asignar jugador: ${errorMessage}`);
    } finally {
      setAssigning(false);
      setSelectedPlayer(null);
    }
  };

  const handleCancelSelection = () => {
    setSelectedPlayer(null);
  };

  // Filtrar futbolistas según el término de búsqueda
  const futbolistasFiltrados = futbolistasDisponibles.filter((jugador) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return jugador.nombre?.toLowerCase().includes(searchLower);
  });

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050
      }}
      onClick={onHide}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem',
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f8f9fa'
          }}
        >
          <h5 style={{ margin: 0, fontWeight: 600 }}>
            <FaUserPlus className="me-2" />
            Añadir Futbolista - {selectedTeam?.nombre}
          </h5>
          <button
            onClick={onHide}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '1rem',
            maxHeight: 'calc(80vh - 140px)',
            overflowY: 'auto'
          }}
        >
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Cargando futbolistas disponibles...
            </div>
          ) : futbolistasDisponibles.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No hay futbolistas disponibles para asignar</p>
            </div>
          ) : (
            <div>
              <p className="mb-3 text-muted">
                Selecciona un futbolista para añadir al equipo <strong>{selectedTeam?.nombre}</strong>:
              </p>

              {/* Buscador */}
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FaSearch className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar futbolista por nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderLeft: 'none'
                    }}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm('')}
                      style={{ borderLeft: 'none' }}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <small className="text-muted">
                    {futbolistasFiltrados.length} de {futbolistasDisponibles.length} futbolistas
                  </small>
                )}
              </div>

              <div className="row g-2">
                {futbolistasFiltrados.map((jugador) => (
                  <div key={jugador.futbolista_id} className="col-12">
                    <div
                      className="card h-100"
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        border: '1px solid #dee2e6',
                        opacity: jugador.origen === 'fuera_plazo' ? 0.85 : 1,
                        backgroundColor: jugador.origen === 'fuera_plazo' ? '#fef5e7' : 'white'
                      }}
                      onMouseEnter={(e) => {
                        if (jugador.origen !== 'fuera_plazo') {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.borderColor = '#007bff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (jugador.origen !== 'fuera_plazo') {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#dee2e6';
                        }
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <strong style={{ fontSize: '14px' }}>{jugador.nombre}</strong>
                              <span
                                className="ms-2"
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  color: jugador.origen === 'inscrito' ? (jugador.invitado ? '#9b59b6' : '#27ae60') :
                                         jugador.origen === 'no_asignado' ? '#f39c12' :
                                         jugador.origen === 'fuera_plazo' ? '#e67e22' : '#3498db'
                                }}
                              >
                                {
                                  jugador.origen === 'inscrito' ? (jugador.invitado ? '(Invitado Previo)' : '(Inscrito)') :
                                  jugador.origen === 'no_asignado' ? '(No Asignado)' :
                                  jugador.origen === 'fuera_plazo' ? '(Fuera Plazo)' : '(Invitado)'
                                }
                              </span>
                              {jugador.saldo > 0 && (
                                <small className="text-muted ms-2" style={{ fontSize: '10px' }}>
                                  - Saldo: €{jugador.saldo}
                                </small>
                              )}
                            </div>
                            <small className="text-muted">
                              {jugador.ano_nacimiento} • {jugador.edad} años
                              {jugador.club_nombre && ` • ${jugador.club_nombre}`}
                              {jugador.division_nombre && ` • ${jugador.division_nombre}`}
                            </small>
                            {(jugador.posicion_principal || jugador.posicion_secundaria) && (
                              <div>
                                <small className="text-info">
                                  ⚽ {jugador.posicion_principal}
                                  {jugador.posicion_secundaria && jugador.posicion_secundaria !== jugador.posicion_principal && (
                                    <span> / {jugador.posicion_secundaria}</span>
                                  )}
                                </small>
                              </div>
                            )}
                          </div>
                          {selectedPlayer && selectedPlayer.futbolista_id === jugador.futbolista_id ? (
                            // Mostrar selector de posición inline
                            <div style={{ minWidth: '200px' }}>
                              <PositionSelector
                                jugador={selectedPlayer}
                                onConfirm={handleAssignPlayerWithPosition}
                                onCancel={handleCancelSelection}
                                loading={assigning}
                                compact={true}
                              />
                            </div>
                          ) : (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleSelectPlayer(jugador.futbolista_id)}
                              disabled={assigning || selectedPlayer}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                              <FaUserPlus className="me-1" />
                              Seleccionar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mensaje cuando no hay resultados de búsqueda */}
              {searchTerm && futbolistasFiltrados.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">
                    No se encontraron futbolistas con el nombre &quot;{searchTerm}&quot;
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem',
            borderTop: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={assigning}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddPlayerModal;
